import { toast } from "react-toastify";

import BulkAction from "common/components/BulkAction";
import ProfileImage from "common/components/ui/ProfileImage";
import { typedObjectEntries } from "common/helpers/utils";
import type { LinkedInProfile, SelectedFacetParam } from "common/types";

import type { ListLeadsSearchParams } from "./types";

export function startListLeadsBulkAction(
  actionFn: (
    lead: LinkedInProfile,
    abortSignal: AbortSignal,
  ) => Promise<unknown>,
  leads: LinkedInProfile[],
  renderToastTitle: (finishedItemsCount: number) => string,
) {
  toast(
    ({ closeToast }) => (
      <BulkAction<LinkedInProfile>
        actionFn={actionFn}
        items={leads}
        onClose={closeToast}
        renderTitle={renderToastTitle}
        renderReviewItem={({ picture_url, full_name, occupation }) => (
          <div className="flex border border-black-200 p-3 gap-x-2 rounded-3xl">
            <ProfileImage src={picture_url} />
            <div className="flex flex-col gap-y-0.5">
              <span className="text-sm-bold">{full_name}</span>
              <span className="text-black-500 text-xs-medium">
                {occupation}
              </span>
            </div>
          </div>
        )}
      />
    ),
    {
      position: "bottom-right",
      autoClose: false,
      closeButton: false,
      icon: false,
    },
  );
}

//the api takes the selected facets as plain ids
export function parseListLeadsSearchParams({
  keyword,
  premium,
  ...facetSearchParams
}: ListLeadsSearchParams<SelectedFacetParam>) {
  const parsedFacetParams = typedObjectEntries(facetSearchParams).reduce(
    (accumulator, [key, selection]) => {
      accumulator[key] = selection.map(({ id }) => id);
      return accumulator;
    },
    {} as Record<keyof typeof facetSearchParams, string[]>,
  );

  return {
    ...parsedFacetParams,
    keyword: keyword.trim(),
    //premium is the one facet the api takes as a boolean instead of ids, so it
    //only filters when exactly one of its two values is selected
    ...(premium.length === 1 && { premium: premium[0].id === "true" }),
  };
}

// Detects clicks on an actions trigger or its portaled menu/dialog content
// (dropdown menu, drawer, confirmation dialog). These are logically nested
// inside a clickable row/link, but their content renders in a portal, so
// this lets the row/link ignore such clicks instead of every trigger and
// portaled element having to stop the click from propagating.
export function isActionsElementClick(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    !!target.closest(
      '[aria-haspopup], [role="menu"], [role="menuitem"], [role="dialog"]',
    )
  );
}
